/**
 * Admin root component.
 *
 * URL-backed routing for the admin dashboard:
 * - / → login or restore last workspace
 * - /workspaces → workspace selector
 * - /workspaces/:publicWorkspaceId/:page → admin shell pages
 */

import { useState, useCallback, useEffect, type ReactNode } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import { useAdminAuth } from './auth/AdminAuthProvider';
import { useWorkspace } from './hooks/useWorkspace';
import * as api from './api-client';
import { AdminShell } from './components/AdminShell';
import { LoginPage } from './pages/LoginPage';
import { WorkspaceSelectorPage } from './pages/WorkspaceSelectorPage';
import { OverviewPage } from './pages/OverviewPage';
import { PerformancePage } from './pages/PerformancePage';
import { ConversationsPage } from './pages/ConversationsPage';
import { ConversationDetailPage } from './pages/ConversationDetailPage';
import { TeamAccessPage } from './pages/TeamAccessPage';
import { MatcoraBrandMark } from './components/MatcoraBrand';

type WorkspacePage = 'overview' | 'performance' | 'conversations' | 'team';

const VALID_PAGES = new Set<WorkspacePage>([
  'overview',
  'performance',
  'conversations',
  'team',
]);

export function App() {
  const { isInitialized } = useAdminAuth();

  useEffect(() => {
    document.body.setAttribute('data-admin-ready', 'true');
    return () => {
      document.body.removeAttribute('data-admin-ready');
    };
  }, []);

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route path="/" element={<RootPage />} />
      <Route
        path="/workspaces"
        element={
          <RequireAuth>
            <WorkspaceSelectorPage />
          </RequireAuth>
        }
      />
      <Route
        path="/workspaces/:publicWorkspaceId"
        element={<WorkspacePageRedirect />}
      />
      <Route
        path="/workspaces/:publicWorkspaceId/:page"
        element={
          <RequireAuth>
            <WorkspaceRouteEntry />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RootPage() {
  const { isAuthenticated } = useAdminAuth();
  const { restoreWorkspaceSelection, clearWorkspace } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      clearWorkspace();
      return;
    }

    let cancelled = false;
    async function resolveEntryRoute() {
      const next = new URLSearchParams(location.search).get('next');
      if (next && next.startsWith('/')) {
        navigate(next, { replace: true });
        return;
      }

      const detail = await restoreWorkspaceSelection();
      if (cancelled) return;

      if (detail?.public_workspace_id) {
        navigate(`/workspaces/${encodeURIComponent(detail.public_workspace_id)}/overview`, {
          replace: true,
        });
        return;
      }

      navigate('/workspaces', { replace: true });
    }

    void resolveEntryRoute();
    return () => {
      cancelled = true;
    };
  }, [clearWorkspace, isAuthenticated, location.search, navigate, restoreWorkspaceSelection]);

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <LoadingScreen />;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAdminAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/?next=${encodeURIComponent(next)}`} replace />;
  }

  return <>{children}</>;
}

function WorkspacePageRedirect() {
  const { publicWorkspaceId } = useParams();
  if (!publicWorkspaceId) {
    return <Navigate to="/workspaces" replace />;
  }
  return <Navigate to={`/workspaces/${encodeURIComponent(publicWorkspaceId)}/overview`} replace />;
}

function WorkspaceRoutePage() {
  const { publicWorkspaceId, page } = useParams();
  const navigate = useNavigate();
  const { workspace, selectWorkspace } = useWorkspace();
  const [isResolving, setIsResolving] = useState(true);
  const [resolutionError, setResolutionError] = useState<string | null>(null);

  useEffect(() => {
    if (!publicWorkspaceId) return;

    if (!page || !VALID_PAGES.has(page as WorkspacePage)) {
      navigate(`/workspaces/${encodeURIComponent(publicWorkspaceId)}/overview`, { replace: true });
      return;
    }

    if (workspace?.public_workspace_id === publicWorkspaceId) {
      setIsResolving(false);
      setResolutionError(null);
      return;
    }

    let cancelled = false;
    async function resolveWorkspace() {
      setIsResolving(true);
      setResolutionError(null);
      try {
        const workspaces = await api.listWorkspaces();
        const match = workspaces.find(
          (candidate) => candidate.public_workspace_id === publicWorkspaceId,
        );
        if (!match) {
          if (!cancelled) {
            setResolutionError('Workspace not found or access denied.');
            setIsResolving(false);
          }
          return;
        }
        if (!match.public_workspace_id) {
          if (!cancelled) {
            setResolutionError('Workspace configuration is incomplete.');
            setIsResolving(false);
          }
          return;
        }

        await selectWorkspace(match.workspace_key, match.public_workspace_id);
        if (!cancelled) {
          setIsResolving(false);
        }
      } catch (err) {
        if (!cancelled) {
          setResolutionError(
            err instanceof api.AdminApiError
              ? err.detail
              : 'Failed to resolve the requested workspace.',
          );
          setIsResolving(false);
        }
      }
    }

    void resolveWorkspace();
    return () => {
      cancelled = true;
    };
  }, [navigate, page, publicWorkspaceId, selectWorkspace, workspace?.public_workspace_id]);

  if (!publicWorkspaceId || !page) {
    return <Navigate to="/workspaces" replace />;
  }

  if (resolutionError) {
    return <WorkspaceAccessError message={resolutionError} />;
  }

  if (isResolving || !workspace || workspace.public_workspace_id !== publicWorkspaceId) {
    return <LoadingScreen />;
  }

  return <WorkspaceShellPage page={page as WorkspacePage} />;
}

function WorkspaceShellPage({ page }: { page: WorkspacePage }) {
  const navigate = useNavigate();
  const { workspace } = useWorkspace();
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [visibleConversationIds, setVisibleConversationIds] = useState<string[]>([]);

  useEffect(() => {
    if (page !== 'conversations') {
      setSelectedConversationId(null);
      setVisibleConversationIds([]);
    }
  }, [page]);

  const handleNavigate = useCallback(
    (nextPage: string) => {
      setSelectedConversationId(null);
      const destination = nextPage === 'workspaces'
        ? '/workspaces'
        : workspace && VALID_PAGES.has(nextPage as WorkspacePage)
          ? `/workspaces/${encodeURIComponent(workspace.public_workspace_id)}/${nextPage}`
          : null;

      if (nextPage === 'workspaces') {
        if (page === 'conversations') {
          window.location.assign('/workspaces');
          return;
        }
        navigate('/workspaces');
        return;
      }
      if (!destination) {
        return;
      }
      if (page === 'conversations' && nextPage !== 'conversations') {
        window.location.assign(destination);
        return;
      }
      navigate(destination);
    },
    [navigate, page, workspace],
  );

  const handleViewConversation = useCallback((conversationId: string) => {
    setSelectedConversationId(conversationId);
  }, []);

  const handleVisibleConversationIdsChange = useCallback((conversationIds: string[]) => {
    setVisibleConversationIds(conversationIds);
  }, []);

  const handleBackToConversations = useCallback(() => {
    setSelectedConversationId(null);
  }, []);

  const handleOpenNextConversation = useCallback(() => {
    if (!selectedConversationId) return;
    const currentIndex = visibleConversationIds.indexOf(selectedConversationId);
    if (currentIndex === -1) return;
    const nextConversationId = visibleConversationIds[currentIndex + 1];
    if (nextConversationId) {
      setSelectedConversationId(nextConversationId);
    }
  }, [selectedConversationId, visibleConversationIds]);

  const handleOpenPreviousConversation = useCallback(() => {
    if (!selectedConversationId) return;
    const currentIndex = visibleConversationIds.indexOf(selectedConversationId);
    if (currentIndex <= 0) return;
    const previousConversationId = visibleConversationIds[currentIndex - 1];
    if (previousConversationId) {
      setSelectedConversationId(previousConversationId);
    }
  }, [selectedConversationId, visibleConversationIds]);

  const currentConversationIndex = selectedConversationId
    ? visibleConversationIds.indexOf(selectedConversationId)
    : -1;
  const hasPreviousConversation = currentConversationIndex > 0;
  const hasNextConversation = selectedConversationId
    ? currentConversationIndex >= 0 && currentConversationIndex < visibleConversationIds.length - 1
    : false;

  let pageContent: ReactNode;
  if (page === 'performance') {
    pageContent = <PerformancePage />;
  } else if (page === 'team') {
    pageContent = <TeamAccessPage />;
  } else if (page === 'conversations') {
    pageContent = selectedConversationId ? (
      <ConversationDetailPage
        conversationId={selectedConversationId}
        onBack={handleBackToConversations}
        onOpenPrevious={handleOpenPreviousConversation}
        onOpenNext={handleOpenNextConversation}
        hasPreviousConversation={hasPreviousConversation}
        hasNextConversation={hasNextConversation}
      />
    ) : (
      <ConversationsPage
        onViewConversation={handleViewConversation}
        onVisibleConversationIdsChange={handleVisibleConversationIdsChange}
      />
    );
  } else {
    pageContent = <OverviewPage />;
  }

  return (
    <AdminShell activePage={page} onNavigate={handleNavigate}>
      {pageContent}
    </AdminShell>
  );
}

function WorkspaceRouteEntry() {
  const location = useLocation();
  return <WorkspaceRoutePage key={location.pathname} />;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--surface-primary)]">
      <div className="text-center">
        <MatcoraBrandMark className="mx-auto mb-4 h-16 w-16 animate-pulse shadow-[0_12px_24px_rgba(15,23,42,0.14)]" />
        <p className="text-[var(--text-secondary)]">Loading...</p>
      </div>
    </div>
  );
}

function WorkspaceAccessError({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--surface-primary)] p-4">
      <div className="max-w-md w-full rounded-[1.5rem] border border-[var(--border-default)] bg-white p-6 text-center shadow-[0_12px_28px_rgba(15,23,42,0.06)]">
        <MatcoraBrandMark className="mx-auto mb-4 h-16 w-16 shadow-[0_12px_24px_rgba(15,23,42,0.14)]" />
        <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Workspace unavailable</h1>
        <p className="mt-3 text-sm leading-6 text-[var(--text-secondary)]">{message}</p>
        <a
          href="/workspaces"
          className="mt-6 inline-flex items-center justify-center rounded-xl bg-[var(--accent-primary)] px-4 py-3 text-sm font-medium text-white hover:opacity-90"
        >
          Back to workspaces
        </a>
      </div>
    </div>
  );
}
